"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const starkware_crypto_1 = require("@authereum/starkware-crypto");
const enc_utils_1 = require("enc-utils");
const bn_js_1 = tslib_1.__importDefault(require("bn.js"));
const ethers_1 = require("ethers");
const ethersproject_hardware_wallets_1 = require("@authereum/ethersproject-hardware-wallets");
const DEFAULT_ACCOUNT_MAPPING_KEY = 'STARKWARE_ACCOUNT_MAPPING';
function getJsonRpcProvider(provider) {
    return typeof provider === 'string'
        ? new ethers_1.providers.JsonRpcProvider(provider)
        : provider;
}
const ETH_STANDARD_PATH = "m/44'/60'/0'/0";
function getPath(index = 0) {
    return `${ETH_STANDARD_PATH}/${index}`;
}
class MemoryStore {
    constructor() {
        this.storage = {};
        this.set = (key, data) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.storage[key] = data;
        });
        this.get = (key) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.storage[key];
        });
        this.remove = (key) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            delete this.storage[key];
        });
    }
}
class StarkwareWallet {
    constructor(mnemonicOrPrivateKeyOrSigner, provider, store = new MemoryStore(), accountMappingKey = DEFAULT_ACCOUNT_MAPPING_KEY) {
        this.mnemonic = '';
        this.privateKey = '';
        this._layer = '';
        this._application = '';
        this._index = '0';
        this.walletIndex = 0;
        this._debug = false;
        this._debugLog = (...args) => {
            if (!this._debug) {
                return;
            }
            console.debug('[stark-wallet]', ...args);
        };
        if (mnemonicOrPrivateKeyOrSigner instanceof ethersproject_hardware_wallets_1.LedgerSigner) {
            this.signer = mnemonicOrPrivateKeyOrSigner;
        }
        else if (mnemonicOrPrivateKeyOrSigner.length > 66) {
            this.mnemonic = mnemonicOrPrivateKeyOrSigner;
        }
        else {
            this.privateKey = enc_utils_1.sanitizeHex(mnemonicOrPrivateKeyOrSigner);
        }
        this.provider = getJsonRpcProvider(provider);
        this.store = store;
        this.accountMappingKey = accountMappingKey;
    }
    setDebug(debug) {
        this._debug = debug;
    }
    static fromLedger(path = ETH_STANDARD_PATH, provider, ethApp) {
        const type = 'hid';
        provider = getJsonRpcProvider(provider);
        if (!path) {
            path = ETH_STANDARD_PATH;
        }
        const index = 0;
        const hdPath = `${path}/${index}`;
        const signer = new ethersproject_hardware_wallets_1.LedgerSigner(provider, type, hdPath, ethApp);
        return new StarkwareWallet(signer, provider);
    }
    static fromSigner(signer, provider, store, accountMappingKey = DEFAULT_ACCOUNT_MAPPING_KEY) {
        return new StarkwareWallet(signer, provider, store, accountMappingKey);
    }
    static fromPrivateKey(privateKey, provider, store = new MemoryStore(), accountMappingKey = DEFAULT_ACCOUNT_MAPPING_KEY) {
        return new StarkwareWallet(privateKey, provider, store, accountMappingKey);
    }
    static fromSignature(signature, provider, store = new MemoryStore(), accountMappingKey = DEFAULT_ACCOUNT_MAPPING_KEY) {
        const privateKey = starkware_crypto_1.privateKeyFromSignature(signature);
        return StarkwareWallet.fromPrivateKey(privateKey, provider, store, accountMappingKey);
    }
    setProvider(provider) {
        this._debugLog('setProvider', provider);
        this.provider = getJsonRpcProvider(provider);
    }
    setPath(path) {
        this._debugLog('setPath', path);
        if (this.signer) {
            this.signer.setPath(path);
        }
    }
    setWalletIndex(walletIndex) {
        this._debugLog('setWalletIndex', walletIndex);
        this.walletIndex = walletIndex;
    }
    getStarkPublicKey(path) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getStarkPublicKey', path);
            if (this.signer) {
                return this.signer.getStarkPublicKey(path);
            }
            const keyPair = yield this.getKeyPairFromPath(path);
            const starkPublicKey = starkware_crypto_1.getStarkPublicKey(keyPair);
            return starkPublicKey;
        });
    }
    getActiveKeyPair() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getActiveKeyPair');
            yield this.getAccountMapping();
            if (this.activeKeyPair) {
                return this.activeKeyPair;
            }
            else {
                throw new Error('No Active Starkware KeyPair - please provide a path');
            }
        });
    }
    getEthereumAddress() {
        this._debugLog('getEthereumAddress');
        return this.getAddress();
    }
    account(layer, application, index) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('account', layer, application, index);
            this._layer = layer;
            this._application = application;
            this._index = index;
            if (!application) {
                console.warn('application is undefined');
            }
            let path = yield this.getAccountPath(layer, application, index);
            return this.getStarkKey(path);
        });
    }
    getAccountPath(layer = this._layer, application = this._application, index = this._index) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getAccountPath');
            const ethAddress = yield this.getEthereumAddress();
            this._debugLog('getAccountPath', layer, application, index, ethAddress);
            const path = starkware_crypto_1.getAccountPath(layer, application, ethAddress, index);
            return path;
        });
    }
    getStarkKey(path) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getStarkKey', path);
            const starkPublicKey = yield this.getStarkPublicKey(path);
            return enc_utils_1.sanitizeHex(starkware_crypto_1.getXCoordinate(starkPublicKey));
        });
    }
    sign(message) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('sign', message);
            const path = yield this.getAccountPath();
            if (this.signer) {
                return this.signer.starkSign(path, message);
            }
            const keyPair = yield this.getKeyPairFromPath(path);
            return starkware_crypto_1.sign(keyPair, message);
        });
    }
    signWithEthereumKey(message) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            const sig = yield wallet.signMessage(message);
            return sig;
        });
    }
    signTransaction(unsignedTx) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const wallet = this.getWallet();
            const signedTx = yield wallet.signTransaction(unsignedTx);
            return signedTx;
        });
    }
    sendTransaction(unsignedTx) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('sendTransaction', unsignedTx);
            const wallet = this.getWallet();
            unsignedTx = Object.assign({}, unsignedTx);
            if (unsignedTx.value) {
                if (!enc_utils_1.isHexString(unsignedTx.value)) {
                    unsignedTx.value = enc_utils_1.sanitizeHex(new bn_js_1.default(unsignedTx.value, 10).toString('hex'));
                }
            }
            const populatedTx = yield wallet.populateTransaction(unsignedTx);
            const tx = yield wallet.sendTransaction(populatedTx);
            return tx;
        });
    }
    getAddress() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getAddress');
            const wallet = this.getWallet();
            this._debugLog('getAddress wallet', wallet);
            return wallet.getAddress();
        });
    }
    getKeyPairFromPath(path) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this._debugLog('getKeyPairFromPath', path);
            const accountMapping = yield this.getAccountMapping();
            if (this.privateKey) {
                const activeKeyPair = starkware_crypto_1.getKeyPair(this.privateKey);
                yield this.setActiveKeyPair(path, activeKeyPair);
                return activeKeyPair;
            }
            if (!path) {
                return this.getActiveKeyPair();
            }
            const match = accountMapping[path];
            if (match) {
                return starkware_crypto_1.ec.keyFromPrivate(match);
            }
            const activeKeyPair = starkware_crypto_1.getKeyPairFromPath(this.mnemonic, path);
            yield this.setActiveKeyPair(path, activeKeyPair);
            return activeKeyPair;
        });
    }
    setActiveKeyPair(path, activeKeyPair) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const accountMapping = yield this.getAccountMapping();
            accountMapping[path] = activeKeyPair.getPrivate('hex');
            this.accountMapping = accountMapping;
            this.activeKeyPair = activeKeyPair;
            yield this.store.set(this.accountMappingKey, accountMapping);
        });
    }
    getAccountMapping() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (typeof this.accountMapping !== 'undefined') {
                return this.accountMapping;
            }
            const accountMapping = (yield this.store.get(this.accountMappingKey)) || {};
            this.accountMapping = accountMapping;
            const paths = Object.keys(accountMapping);
            if (paths.length && !this.activeKeyPair) {
                this.activeKeyPair = starkware_crypto_1.ec.keyFromPrivate(accountMapping[paths[0]]);
            }
            return accountMapping;
        });
    }
    getWallet() {
        if (this.signer) {
            this._debugLog('getWallet this.signer');
            return this.signer;
        }
        if (this.privateKey) {
            this._debugLog('getWallet this.privateKey');
            this._debugLog('getWallet provider', this.provider);
            try {
                return new ethers_1.Wallet(this.privateKey).connect(this.provider);
            }
            catch (err) {
                return new ethers_1.Wallet(this.privateKey);
            }
        }
        this._debugLog('getWallet fromMnemonic');
        return ethers_1.Wallet.fromMnemonic(this.mnemonic, getPath(this.walletIndex)).connect(this.provider);
    }
}
exports.StarkwareWallet = StarkwareWallet;
exports.default = StarkwareWallet;
//# sourceMappingURL=wallet.js.map